import {
  Box,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import {
  AnimatedChevronIcon,
  BadgeIcon,
  CheckedCheckboxIcon,
  RoofIcon,
  UncheckedCheckboxIcon,
  VisibilityIcon,
  WallTransparencyIcon,
} from '../../Svgs'
import { HideShowOption } from '../../../models/Construction'
import { toggleHideShowOption } from '../../../actions/construction'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../store'
import { useRef, useState } from 'react'
import { getEnums } from '../../../utils/Common'

const HideShowOptions = () => {
  const dispatch = useDispatch<AppDispatch>()

  const hideShowOptions = useSelector(
    (state: RootState) => state.construction.hideShowOptions,
  )

  const boxRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)
  const popoverId = open ? 'hide-show-popover' : undefined

  return (
    <>
      <Box
        ref={boxRef}
        sx={{
          display: 'flex',
          borderRadius: '6px',
          background:
            'radial-gradient(73.19% 50% at 50% 50%, rgba(88, 88, 88, 0.30) 0%, rgba(204, 204, 204, 0.30) 100%)',
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          pointerEvents: 'auto',
          padding: '6px',
        }}
      >
        <ButtonGroup
          size='small'
          sx={{
            background: '#FBFBFB',
            borderRadius: '4px',
            '& > .MuiIconButton-root': {
              minWidth: '60px',
              borderRadius: '4px',
              backgroundColor: '#FBFBFB',
              gap: '8px',
              '&:hover': {
                backgroundColor: '#E2E2E2',
              },
            },
          }}
        >
          <IconButton
            sx={{
              alignItems: 'start',
              padding: '8px 16px 8px 8px',
            }}
            onClick={() =>
              dispatch(toggleHideShowOption(HideShowOption.RoofsVisibility))
            }
          >
            <BadgeIcon
              sx={{
                opacity: hideShowOptions[HideShowOption.RoofsVisibility]
                  ? 1
                  : 0,
                fontSize: '4px',
                color: '#FF8400',
              }}
            />

            <RoofIcon sx={{ fontSize: '24px' }} />
          </IconButton>

          <Divider
            flexItem
            orientation='vertical'
            variant='middle'
            sx={{
              backgroundColor: '#E4E4E4',
            }}
          />

          <IconButton
            sx={{
              alignItems: 'start',
              padding: '8px 16px 8px 8px',
            }}
            onClick={() =>
              dispatch(toggleHideShowOption(HideShowOption.WallsTransparency))
            }
          >
            <BadgeIcon
              sx={{
                opacity: hideShowOptions[HideShowOption.WallsTransparency]
                  ? 1
                  : 0,
                fontSize: '4px',
                color: '#FF8400',
              }}
            />
            <WallTransparencyIcon sx={{ fontSize: '24px' }} />
          </IconButton>

          <Divider
            flexItem
            orientation='vertical'
            variant='middle'
            sx={{
              backgroundColor: '#E4E4E4',
            }}
          />

          <IconButton
            sx={{
              padding: '8px',
            }}
            onClick={() => setAnchorEl(boxRef.current)}
          >
            <VisibilityIcon
              sx={{
                fontSize: '24px',
                color: '#04002E',
              }}
            />

            <AnimatedChevronIcon
              open={open}
              reversed
              sx={{
                color: '#04002E',
                fontSize: '12px',
              }}
            />
          </IconButton>
        </ButtonGroup>
      </Box>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: '-0.5rem',
              boxShadow: 'none',
              background: 'transparent',
              minWidth: '168px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            },
          },
        }}
      >
        <Typography
          sx={{
            color: '#06004A',
            fontFamily: 'Inter',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Hide and Show
        </Typography>

        <Paper
          elevation={0}
          sx={{
            filter: 'drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.25))',
            background: '#FBFBFB',
            borderRadius: '4px',
          }}
        >
          <Stack direction='column' spacing={0}>
            <FormGroup>
              {getEnums(HideShowOption, [
                HideShowOption.RoofsVisibility,
                HideShowOption.WallsTransparency,
              ]).map(([key, value]) => (
                <FormControlLabel
                  key={key}
                  label={value}
                  control={
                    <Checkbox
                      name={key}
                      checked={hideShowOptions[value]}
                      onChange={() => dispatch(toggleHideShowOption(value))}
                      icon={<UncheckedCheckboxIcon />}
                      checkedIcon={<CheckedCheckboxIcon />}
                      sx={{
                        padding: 0,
                        color: '#1705E5',
                        '& .MuiSvgIcon-root': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  }
                  slotProps={{
                    typography: {
                      color: '#04002E',
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                    },
                  }}
                  sx={{
                    margin: 0,
                    gap: '8px',
                    padding: '6px',
                    borderBottom: '1px solid #E4E4E4',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                />
              ))}
            </FormGroup>
          </Stack>
        </Paper>
      </Popover>
    </>
  )
}

export default HideShowOptions
